import { useEffect, useRef, useState } from 'react'
import * as BABYLON from '@babylonjs/core'
import { buildRealisticHall, HallPrintPhoto } from './buildRealisticHall'

export interface UseRealisticHallSceneResult {
  ready: boolean
  insideMode: boolean
  playIntro: (skip?: boolean) => void
  stepOutside: () => void
}

/**
 * Owns the `buildRealisticHall` scene's lifecycle for one `scene`+`photos`
 * pair, and mirrors its `onReady`/`onExploreChange` callbacks into React
 * state so the screen can drive its HUD/loading chrome declaratively.
 * Rebuilds whenever `photos` changes identity (new gallery, or the source
 * album finishing its fetch) — not optimized for hot-swapping single prints.
 */
export function useRealisticHallScene(
  scene: BABYLON.Scene | null,
  engine: BABYLON.Engine | null,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  photos: HallPrintPhoto[],
): UseRealisticHallSceneResult {
  const [ready, setReady] = useState(false)
  const [insideMode, setInsideMode] = useState(false)
  const apiRef = useRef<ReturnType<typeof buildRealisticHall> | null>(null)

  useEffect(() => {
    if (!scene || !engine || !canvasRef.current || photos.length === 0) return
    setReady(false)
    setInsideMode(false)
    const api = buildRealisticHall(scene, engine, canvasRef.current, photos, {
      onReady: () => setReady(true),
      onExploreChange: setInsideMode,
    })
    apiRef.current = api
    return () => {
      apiRef.current = null
      api.dispose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, engine, photos])

  return {
    ready,
    insideMode,
    playIntro: (skip) => apiRef.current?.playIntro(skip),
    stepOutside: () => apiRef.current?.stepOutside(),
  }
}
